import { useMemo } from 'react';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import useGraphQLClient from '../../../../app/hooks/useGraphQLClient';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import AppError from '../../../../app/features/error/services/AppError';
import _ from 'lodash';
import IFundPerformancesSG from '../../interfaces/fund/performances/IFundPerformancesSG';
import moment from 'moment';

const useInvestFundPerformancesSG = (fundId: number | null) => {
	const auth = useAuthContext();
	const graphQLClient = useGraphQLClient();
	const filter = {
		fundId: fundId,
		userId: auth.authOverrideUserId,
	};

	const { data, isFetching } = useQuery(
		['investmentFundPerformancesSG', auth.authOverrideUserId ?? 'null', filter],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query ($fundId: Int!) {
							investmentFundPerformancesSG(fundId: $fundId) {
								capital {
									dataUpdatedOn
									title
									fundName
									data {
										date
										share
										performance
									}
								}
								cumulativePerformances {
									dateType
									value
									bench
								}
								annualizedPerformances {
									dateType
									value
									bench
								}
								annualPerformances {
									dateType
									value
									bench
								}
								lastKpi {
									assetTotalValue
									navPerShare
								}
							}
						}
					`,
					filter,
				)) as { investmentFundPerformancesSG: IFundPerformancesSG };
				return data.investmentFundPerformancesSG;
			} catch (e) {
				throw new AppError(e, 'useInvestFundPerformances');
			}
		},
		{
			enabled:
				!!filter.fundId && auth.isInvestor === true && auth.isAuthenticated && auth.currentCompany !== null,
		},
	);

	const performances = useMemo<IFundPerformancesSG | undefined>(() => {
		if (!data) return undefined;
		const dataTmp: IFundPerformancesSG = _.cloneDeep(data);
		if (dataTmp.capital) {
			dataTmp.capital.data = dataTmp.capital?.data?.map((x) => ({
				date: moment(x.date).valueOf(),
				share: x.share ? Number(x.share) : x.share,
				performance: x.performance ? Number(x.performance) : x.performance,
			}));
		}
		return dataTmp;
	}, [data]);

	return {
		isFetching,
		performances,
	};
};

export default useInvestFundPerformancesSG;
